// ============================================================
//  WINROX11 ADMIN PANEL — SHARED DESIGN TOKENS
// ============================================================

export const COLORS = {
  bg: "#080c14",
  surface: "#0e1422",
  card: "#111827",
  cardBorder: "#1e293b",
  cardHover: "#182030",
  indigo: "#6366f1",
  indigoLight: "#818cf8",
  indigoDim: "#6366f115",
  indigoBorder: "#6366f130",
  green: "#22c55e",
  red: "#ef4444",
  amber: "#f59e0b",
  purple: "#a855f7",
  text: "#e2e8f0",
  textMid: "#94a3b8",
  textMuted: "#64748b",
  textDim: "#475569",
  inputBg: "#0a0f1c",
  inputBorder: "#1e293b",
  roleBat: "#3b82f6",
  roleBowl: "#ef4444",
  roleAr: "#10b981",
  roleWk: "#f59e0b",
};

export const getRoleColor = (role) => {
  const map = {
    BAT: COLORS.roleBat,
    BOWL: COLORS.roleBowl,
    AR: COLORS.roleAr,
    WK: COLORS.roleWk,
  };
  return map[(role || "").toUpperCase()] || COLORS.indigo;
};

export const S = {
  // Layout
  page: {
    minHeight: "100vh",
    background: COLORS.bg,
    color: COLORS.text,
    fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
    padding: "36px",
    maxWidth: "1280px",
    margin: "0 auto",
  },
  // Top bar
  topBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "32px",
  },
  pageTitle: {
    fontSize: "26px",
    fontWeight: 800,
    letterSpacing: "-0.5px",
    color: COLORS.text,
  },
  pageSubtitle: {
    fontSize: "11px",
    color: COLORS.indigo,
    fontWeight: 700,
    letterSpacing: "2px",
    textTransform: "uppercase",
    marginTop: "4px",
  },
  // Back button
  backBtn: {
    background: "none",
    border: "none",
    color: COLORS.indigo,
    cursor: "pointer",
    fontWeight: 700,
    fontSize: "12px",
    letterSpacing: "1px",
    textTransform: "uppercase",
    marginBottom: "20px",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: 0,
  },
  // Cards
  card: {
    background: COLORS.card,
    border: `1px solid ${COLORS.cardBorder}`,
    borderRadius: "16px",
    padding: "28px",
    marginBottom: "28px",
  },
  cardTitle: {
    fontSize: "11px",
    fontWeight: 700,
    color: COLORS.indigoLight,
    letterSpacing: "2px",
    textTransform: "uppercase",
    marginBottom: "20px",
  },
  // Form elements
  label: {
    fontSize: "11px",
    fontWeight: 600,
    color: COLORS.textMuted,
    letterSpacing: "1.5px",
    textTransform: "uppercase",
    marginBottom: "6px",
    display: "block",
  },
  input: {
    width: "100%",
    padding: "12px 16px",
    background: COLORS.inputBg,
    border: `1px solid ${COLORS.inputBorder}`,
    borderRadius: "10px",
    color: COLORS.text,
    fontSize: "14px",
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "'Inter', sans-serif",
  },
  select: {
    width: "100%",
    padding: "12px 16px",
    background: COLORS.inputBg,
    border: `1px solid ${COLORS.inputBorder}`,
    borderRadius: "10px",
    color: COLORS.text,
    fontSize: "14px",
    outline: "none",
    appearance: "auto",
    boxSizing: "border-box",
    fontFamily: "'Inter', sans-serif",
  },
  // Button row
  btnRow: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    marginTop: "20px",
  },
  btnPrimary: {
    padding: "11px 28px",
    background: COLORS.indigo,
    border: "none",
    borderRadius: "10px",
    color: "white",
    fontWeight: 700,
    fontSize: "13px",
    cursor: "pointer",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    fontFamily: "'Inter', sans-serif",
  },
  btnWarning: {
    padding: "11px 28px",
    background: COLORS.amber,
    border: "none",
    borderRadius: "10px",
    color: "#000",
    fontWeight: 700,
    fontSize: "13px",
    cursor: "pointer",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    fontFamily: "'Inter', sans-serif",
  },
  btnDanger: {
    padding: "11px 28px",
    background: COLORS.red,
    border: "none",
    borderRadius: "10px",
    color: "white",
    fontWeight: 700,
    fontSize: "13px",
    cursor: "pointer",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    fontFamily: "'Inter', sans-serif",
  },
  btnCancel: {
    padding: "11px 24px",
    background: "transparent",
    border: `1px solid ${COLORS.inputBorder}`,
    borderRadius: "10px",
    color: COLORS.textMid,
    fontWeight: 600,
    fontSize: "13px",
    cursor: "pointer",
    textTransform: "uppercase",
    fontFamily: "'Inter', sans-serif",
  },
  // Mini action buttons
  miniBtn: {
    width: "30px",
    height: "30px",
    borderRadius: "8px",
    border: `1px solid ${COLORS.cardBorder}`,
    background: COLORS.inputBg,
    color: COLORS.textMid,
    fontSize: "13px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  // Status badges
  badge: (color) => ({
    fontSize: "9px",
    fontWeight: 700,
    padding: "3px 10px",
    borderRadius: "6px",
    textTransform: "uppercase",
    letterSpacing: "1px",
    background: color + "20",
    color: color,
    border: `1px solid ${color}30`,
    display: "inline-block",
  }),
  // Section title
  sectionTitle: {
    fontSize: "11px",
    fontWeight: 700,
    color: COLORS.indigoLight,
    letterSpacing: "2px",
    textTransform: "uppercase",
    marginBottom: "16px",
  },
  // Empty state
  empty: {
    textAlign: "center",
    padding: "60px 20px",
    color: COLORS.textDim,
    background: COLORS.card,
    borderRadius: "16px",
    border: `1px solid ${COLORS.cardBorder}`,
    fontSize: "14px",
  },
  // Loading state
  loading: {
    textAlign: "center",
    padding: "60px 20px",
    color: COLORS.textMuted,
    fontSize: "14px",
    fontStyle: "italic",
  },
};

export const GLOBAL_ADMIN_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

  * { box-sizing: border-box; }

  ::-webkit-scrollbar { width: 6px; height: 6px; }
  ::-webkit-scrollbar-track { background: #0a0f1c; }
  ::-webkit-scrollbar-thumb { background: #1e293b; border-radius: 3px; }
  ::-webkit-scrollbar-thumb:hover { background: #374151; }

  input[type="number"]::-webkit-inner-spin-button,
  input[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  input[type="number"] {
    -moz-appearance: textfield;
  }

  input::placeholder, textarea::placeholder { color: #334155; }

  .admin-input-focus:focus {
    border-color: #6366f1 !important;
    box-shadow: 0 0 0 3px rgba(99,102,241,0.12) !important;
  }

  .admin-btn-hover:hover {
    filter: brightness(1.12);
    transform: translateY(-1px);
  }

  .admin-card-hover:hover {
    border-color: #374151 !important;
    background: #182030 !important;
    transform: translateY(-2px);
  }

  .admin-row-hover:hover { background: #0e1422 !important; }

  @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
  @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
`;
